import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import LGModalCrud from "../LGCrud/LGModalCrud";
import ExportForm from "./ExportForm";
import dayjs from "dayjs";
import { FileDownload } from "@mui/icons-material";
import LGModal from "../../molecules/LGModal";
import ExportFilesTable from "./ExportFilesTable";
import ExportDocumentsModal from "./ExportDocumentsModal";
import { useNavigate } from "react-router-dom";
import { getExportList, deleteExport, downloadCommercialInvoice, downloadJorgiFactura } from "../../api/exports";
import { handleDownload } from "../../services/PDFHandler.ts";
import { useSnack } from "../../services/SnakbarProvider";

export default function CrudExport({ companyId }) {
  const { openSnack } = useSnack();
  const navigate = useNavigate();
  const [exportModalOpen, setExportModalOpen] = useState(false);

  const handleInvoiceDownload = async (id) => {
    try {
      const response = await downloadCommercialInvoice(id);
      handleDownload(
        response,
        "commercial_invoice",
        "No se pudo generar la factura comercial",
        () => openSnack("No se pudo generar la factura comercial", "error")
      );
    } catch {
      openSnack("No se pudo generar la factura comercial", "error");
    }
  };

  const handleJorgiFacturaDownload = async (id) => {
    try {
      const response = await downloadJorgiFactura(id);
      handleDownload(
        response,
        "jorgi_factura.xlsx",
        "No se pudo descargar JorgiFactura",
        () => openSnack("No se pudo descargar JorgiFactura", "error")
      );
    } catch {
      openSnack("No se pudo descargar JorgiFactura", "error");
    }
  };

  const setStatusColor = (statusCode) => {
    switch (statusCode) {
      case "CREADO":
        return "default";
      case "EN_PROCESO":
        return "warning"; 
      case "COMPLETADO":
        return "success";
      default:
        return "default";
    }
  };

  return (
    <Box width={"100%"}>
      <LGModal
        open={exportModalOpen}
        handleClose={() => setExportModalOpen(false)}
        header="Exportar a CSV"
      >
        {/* Add export modal content here if needed */}
      </LGModal>
      
      <Stack direction="row" spacing={2} mb={2}>
        <Button 
          onClick={() => setExportModalOpen(true)} 
          variant="contained" 
          startIcon={<FileDownload />}
        >
          Exportar CSV
        </Button>
      </Stack>

      <LGModalCrud
        props={{
          actions: ["create", "update", "delete", "detail", "download"],
          data: { companyId },
          fetchFunction: getExportList,
          deleteFunction: deleteExport,
          downloadFunction: handleInvoiceDownload,
          detailMenu: [
            {
              label: "Ver contenido",
              onClick: (id) => navigate("/exports/items", { state: { id } }),
            },
            {
              label: "Documentos",
              handler: () => {},
              header: "Documentos",
              body: <ExportFilesTable />,
            },
            {
              label: "JorgiFactura",
              onClick: (id) => handleJorgiFacturaDownload(id),
            },
            {
              label: "Descargar documentos",
              header: "Descargar documentos",
              body: <ExportDocumentsModal />,
            },
          ],
          columns: [
            {
              field: "code",
              headerName: "Código",
              flex: 0.2,
            },
            {
              field: "consignee",
              headerName: "Cliente",
              flex: 0.2,
              valueGetter: (value, row) => row.consignee?.name || "",
            },
            {
              field: "clientPurchaseOrder",
              headerName: "Orden de Compra",
              flex: 0.2,
              renderCell: (params) => (
                <Stack spacing={1}>
                  {params.row.exportItems && params.row.exportItems.map((item, index) => (
                    <Typography key={index} variant="body2" noWrap>
                      {item.clientPurchaseOrder?.code|| "N/A"}
                    </Typography>
                  ))}
                </Stack>
              ),
            },
            {
              field: "invoiceNo",
              headerName: "Factura",
              flex: 0.15,
              valueGetter: (value, row) =>
                row.invoiceNo != null ? String(row.invoiceNo) : "—",
            },
            {
              field: "packingListDate",
              headerName: "Fecha Packing List",
              flex: 0.2,
              type: "date",
              sortable: true,
              filterable: true,
              valueGetter: (value, row) =>
                row?.packingListDate != null && row.packingListDate !== ""
                  ? dayjs(row.packingListDate).toDate()
                  : null,
              valueFormatter: (value) =>
                value != null ? dayjs(value).format("DD/MM/YYYY") : "",
            },
            {
              field: "quantity",
              headerName: "Cantidad",
              flex: 0.1,
              renderCell: (params) => (
                <Stack spacing={1}>
                  {params.row.exportItems && params.row.exportItems.map((item, index) => (
                    <Typography key={index} variant="body2" noWrap>
                      {item.itemQuantity || 0}
                    </Typography>
                  ))}
                </Stack>
              ),
            },
            {
              field: "status",
              headerName: "Estatus",
              flex: 0.2,
              renderCell: (params) => (
                <Box
                  mt={2}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Chip
                    size="small"
                    label={params.row.status.name}
                    color={setStatusColor(params.row.status.code)}
                  />
                </Box>
              ),
            },
          ],
          addModalData: {
            header: "Agregar Exportación",
            body: <ExportForm companyId={companyId} />,
          },
          editModalData: {
            header: "Editar Exportación", 
            body: <ExportForm companyId={companyId} />,
          },
        }}
      />
    </Box>
  );
}
